import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";
import { EventManagementFeedFilters, EventListingPage } from "../../models";
import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import EventList from "./eventList";
import Tabs from "pages/common/tabs";
import { tagsApi } from "api/instances";
import { TenantSettingsTagGroup } from "modules/settings";
import SuccessSnackbar from "modules/common/components/snackbars/successSnackbar";
import CreatedByFilter, { CreatedBySelectValues } from "modules/common/components/authoring/createdByFilter";
import { ContentType } from "modules/common/models";
import PublishedEventDialog from "./publishedEventDialog";
import { DraftAction } from "modules/common/components/authoring/models";
import { UserRoleStrings } from "modules/authorization/models";
import { getHighestRole } from "utils/userRoleUtils";
import { QUERY_PARAM_KEYS } from "modules/common/hooks/useQueryParams";

export enum EVENT_MANAGEMENT_TAB_IDS {
    DRAFTS,
    SUBMISSIONS,
    SCHEDULED,
    PUBLISHED,
    ALL
}

class EventListing extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);

        const queryParams = new URLSearchParams(window.location.search);

        this.state = {
            selectedTab: parseInt(queryParams.get(QUERY_PARAM_KEYS.TAB_NUMBER) ?? "0"),
            availableTopics: [],
            topicFilter: []
        };
    }

    public async componentDidMount() {
        await this.getTags();
        this.fetchLists();
    }

    public componentDidUpdate(prevProps: PropsWithRedux) {
        if (this.props.shouldFetch && (this.props.shouldFetch !== prevProps.shouldFetch)) {
            this.fetchLists();
        }

        if (this.props.successMessage.action === DraftAction.Submit && this.state.selectedTab !== EVENT_MANAGEMENT_TAB_IDS.SUBMISSIONS)
            this.onSelectTab(EVENT_MANAGEMENT_TAB_IDS.SUBMISSIONS)
    }

    public render() {
        const { selectedTab } = this.state;

        return (
            <React.Fragment>
                <div className="authoring-tabs-container">
                    <Tabs
                        tabs={[
                            { label: this.getTabLabel("Drafts", this.props.drafts), dotColor: "grey", id: "draft-events-tab-button" },
                            { 
                                label: this.getTabLabel(getHighestRole(this.props.user) === UserRoleStrings.CONTRIBUTOR  ? "Pending Approval" : "Needs Review", this.props.submissions),
                                dotColor: "red",
                                id: "submission-events-tab-button"
                            },
                            { label: this.getTabLabel("Scheduled", this.props.scheduled), dotColor: "yellow", id: "scheduled-events-tab-button" },
                            { label: this.getTabLabel("Published", this.props.published), dotColor: "green", id: "published-events-tab-button" },
                            { label: this.getTabLabel("All", this.props.allEventsList), id: "all-events-tab-button" }
                        ]}
                        selectedTab={selectedTab}
                        onSelectTab={this.onSelectTab}
                    />
                    <CreatedByFilter
                        selectedFilter={this.props.allEventsList.filters.myEventsOnly ? CreatedBySelectValues.VIEW_MY : CreatedBySelectValues.VIEW_ALL}
                        content={ContentType.Event}
                        onSelect={this.onSelectCreatedBy}
                    />
                    
                </div>
                <EventList show={selectedTab === EVENT_MANAGEMENT_TAB_IDS.ALL} page={this.props.allEventsList} fetchPage={this.fetchAllEventList} availableTopics={this.state.availableTopics}/>
                <EventList show={selectedTab === EVENT_MANAGEMENT_TAB_IDS.PUBLISHED} page={this.props.published} fetchPage={this.fetchPublished} availableTopics={this.state.availableTopics} eventIdToPreview={this.props.eventIdToPreview}/>
                <EventList show={selectedTab === EVENT_MANAGEMENT_TAB_IDS.SCHEDULED} page={this.props.scheduled} fetchPage={this.fetchScheduled} availableTopics={this.state.availableTopics}/>
                <EventList show={selectedTab === EVENT_MANAGEMENT_TAB_IDS.SUBMISSIONS} page={this.props.submissions} fetchPage={this.fetchSubmissions} availableTopics={this.state.availableTopics}/>
                <EventList show={selectedTab === EVENT_MANAGEMENT_TAB_IDS.DRAFTS} page={this.props.drafts} fetchPage={this.fetchDrafts} availableTopics={this.state.availableTopics}/>
                {this.props.showPublishDialog && <PublishedEventDialog/>}
                <SuccessSnackbar successMessage={ this.props.successMessage.message || "" } clearSuccessMessage={this.props.clearSuccessMessage}/>
                <ErrorSnackbar errorMessage={this.props.errorMessage} clearErrorMessage={this.props.clearErrorMessage} />
                <LoadingOverlay absolute={true} show={this.props.isDeleting || this.props.isFetching || this.props.isSaving} />
            </React.Fragment>
        );
    }

    private onSelectCreatedBy = (selection: CreatedBySelectValues) => {
        const createdByMeOnly = selection === CreatedBySelectValues.VIEW_MY;
        this.fetchLists(createdByMeOnly);
    }

    private fetchLists = (showOnlyMyEvents?: boolean) => {
        const myEventsOnly = showOnlyMyEvents ?? this.props.allEventsList.filters.myEventsOnly;

        this.fetchAllEventList(1, { ...this.props.allEventsList.filters, myEventsOnly });
        this.fetchDrafts(1, { ...this.props.drafts.filters, myEventsOnly });
        this.fetchSubmissions(1, { ...this.props.submissions.filters, myEventsOnly });
        this.fetchPublished(1, { 
            ...this.props.published.filters,
            myEventsOnly,
            tags: this.props.topicFilter ?? this.props.published.filters.tags
        });
        this.fetchScheduled(1, { ...this.props.scheduled.filters, myEventsOnly });
        this.props.clearShouldFetch();
    }

    private fetchDrafts = (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => {
        !this.props.drafts.isFetching &&
            this.props.getDrafts(pageNumber, filters, pageAmount);
    }

    private fetchSubmissions = (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => {
        !this.props.submissions.isFetching &&
            this.props.getSubmissions(pageNumber, filters, pageAmount);
    }

    private fetchPublished = (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => {
        !this.props.published.isFetching &&
            this.props.getPublished(pageNumber, filters, pageAmount);
    }

    private fetchScheduled = (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => {
        !this.props.scheduled.isFetching &&
            this.props.getScheduled(pageNumber, filters, pageAmount);
    }

    private fetchAllEventList = (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => {
        !this.props.allEventsList.isFetching &&
            this.props.getAllEvents(pageNumber, filters, pageAmount);
    }

    private getTabLabel = (label: string, page: EventListingPage): string => {
        if (!!page.currentPage)
            return `${label} (${page.totalEvents})`;
        return label;
    }

    private onSelectTab = (tabIndex: number) => {
        this.setState({ ...this.state, selectedTab: tabIndex });
    }

    private getTags = async () => {
        let tagsToSet: TenantSettingsTagGroup[] = [];

        try {
            tagsToSet = await tagsApi.getUserTags(false, true);
        } catch (err) {
            tagsToSet = [];
        } finally {
            this.setState({ availableTopics: tagsToSet, topicFilter: this.props.topicFilter });
        }
    }
}


interface ComponentProps {
    eventIdToPreview?: string;
    topicFilter: string[];
}

interface ComponentState {
    selectedTab: number;
    availableTopics: TenantSettingsTagGroup[];
    topicFilter: string[];
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        drafts: state.events.drafts,
        submissions: state.events.submissions,
        allEventsList: state.events.allEventsList,
        published: state.events.published,
        scheduled: state.events.scheduled,
        errorMessage: state.events.errorMessage,
        successMessage: state.events.successMessage,
        isDeleting: state.events.isDeleting,
        isFetching: state.events.isFetching,
        isInitialLoad: state.events.isInitialLoad,
        isSaving: state.events.isSaving,
        showPublishDialog: state.events.shouldShowDialog,
        shouldFetch: state.events.shouldFetch,
        tenantSettings: state.settings.tenantSettings,
        tenant: state.tenant.id,
        user: state.settings.currentUser
    }),
    {
        clearErrorMessage: actions.clearErrorMessage,
        clearSuccessMessage: actions.clearSuccessMessage,
        clearShouldFetch: actions.clearShouldFetch,
        getAllEvents: actions.getAllEvents,
        getDrafts: actions.getDrafts,
        getSubmissions: actions.getSubmissions,
        getPublished: actions.getPublished,
        getScheduled: actions.getScheduled,
        redirectTo: push,
        setToDefaultState: actions.setEventsFeedToDefault
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EventListing);
